import { useEffect, useRef, useState } from "react";
import { SettingsHead } from "../components/AccountTabs";
import { supabase } from "../lib/supabase";
import { COMMUNITY_INCLUDED } from "../lib/communityFeature";
import { Workspace } from "../components/Shell";
import {
  actionFor,
  describeAsker,
  describeStatus,
  parseStatus,
  pendingCount,
  requestsEmptyLine,
  sortForInbox,
  type CoachRequest,
} from "../lib/requests";

/* THE INTAKE INBOX — who has asked to be coached (W5, 2026-09-18).
 *
 * 🔴 **ANSWERING GRANTS NOTHING, AND THE SCREEN SAYS SO RATHER THAN IMPLYING OTHERWISE.**
 * `CLAUDE.md`: *a request is a DOORBELL, not a key.* The phone's `decide()` says the same from the
 * other side — the grant on this table is `update (status, decided_at)` and **no policy anywhere
 * reads it**, so the stamp is a record of an answer and can never be the premise of a permission.
 * Accepting therefore does NOT make somebody a client: the coach still invites, and only the
 * invited person may activate. A button labelled "Accept" that silently enrolled somebody would be
 * the exact hole closed on 2026-09-06.
 *
 * ⚠️ **NO `.eq("coach_id", …)` ANYWHERE HERE.** `coach_requests_coach_read` scopes to
 * `coach_id = auth.uid()`; restating it would be a second copy of a rule that must have exactly
 * one — and unlike the policy, the copy is the one that can be forgotten. The phone's service
 * carries that sentence verbatim; this is the same decision on the other surface.
 */

type State =
  | { status: "loading" }
  | { status: "failed"; message: string }
  | { status: "ready"; rows: CoachRequest[] };

export function Requests({ foreground = true }: { foreground?: boolean }) {
  const [state, setState] = useState<State>({ status: "loading" });
  const [deciding, setDeciding] = useState<string | null>(null);
  const [decideError, setDecideError] = useState<string | null>(null);
  const heading = useRef<HTMLHeadingElement>(null);

  // Every route moves focus on arrival — b4-7's rule, and the one I shipped a live defect against
  // earlier today by forgetting it on `NotACoach`.
  useEffect(() => { if (foreground) heading.current?.focus(); }, [foreground]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase
        .from("coach_requests")
        .select("id, client_id, client_name, client_email, note, status, created_at")
        .order("created_at", { ascending: false });
      if (cancelled) return;
      if (error !== null) { setState({ status: "failed", message: error.message }); return; }
      const rows: CoachRequest[] = [];
      for (const r of data ?? []) {
        const status = parseStatus(r.status);
        // An unrecognised status is DROPPED rather than rendered — see `parseStatus`.
        if (status === null) continue;
        rows.push({
          id: r.id,
          clientId: r.client_id,
          clientName: r.client_name,
          clientEmail: r.client_email,
          note: r.note,
          status,
          askedAtISO: r.created_at,
        });
      }
      setState({ status: "ready", rows: sortForInbox(rows) });
    })();
    return () => { cancelled = true; };
  }, []);

  async function decide(id: string, accepted: boolean) {
    setDeciding(id);
    setDecideError(null);
    const { error } = await supabase
      .from("coach_requests")
      .update({ status: accepted ? "accepted" : "declined", decided_at: new Date().toISOString() })
      .eq("id", id);
    setDeciding(null);
    if (error !== null) {
      // The server's own sentence. The commonest refusal is the policy's `status = 'pending'`
      // requirement on a row somebody else already decided — a stale screen must say what it was
      // told rather than a paraphrase of what it expected.
      setDecideError(error.message);
      return;
    }
    setState((prev) => prev.status !== "ready" ? prev : {
      ...prev,
      rows: sortForInbox(prev.rows.map((r) =>
        r.id === id ? { ...r, status: accepted ? "accepted" : "declined" } : r)),
    });
  }

  const rows = state.status === "ready" ? state.rows : [];
  const waiting = pendingCount(rows);


  return (
    <Workspace>
      {/* PRO WEB STEP 8 — NO COLUMN 2 (Fable's ruling on step 7's F2). It was two links, `Clients` and `Requests (N)`,
          W72's rail grammar under W81's labelled sidebar: it pushed this tab's row to x = 464 where the other five Account
          tabs start at 256. One page, one list — the Account's six tabs are the navigation, the sidebar's Clients is the
          way back, and the count is the page's own `N waiting for an answer.` line. */}
      <main id="main" className="account-page requests-page">
        <SettingsHead current="requests" headingRef={heading} />

        {state.status === "loading" && (
          <p className="ht-callout loading-line" aria-live="polite">Loading…</p>
        )}
        {state.status === "failed" && (
          <p className="ht-callout requests-error" role="alert">{state.message}</p>
        )}
        {decideError !== null && (
          <p className="ht-callout requests-error" role="alert">{decideError}</p>
        )}

        {/* JOB 3 STEP 5 PAGE 6 — ONE quiet line, the Reminders page's own empty shape (it was a dashed, centred box saying
            "publish your coaching page" — a page not drawn while the Community is out). */}
        {state.status === "ready" && rows.length === 0 && (
          <p className="ht-callout ours-quiet requests-empty">{requestsEmptyLine(COMMUNITY_INCLUDED)}</p>
        )}

        {rows.length > 0 && (
          <>
            <p className="ht-caption requests-count">
              {waiting === 0
                ? "Nothing waiting for an answer."
                : `${waiting} waiting for an answer.`}
            </p>
            {/* 🔴 THE SENTENCE THAT KEEPS THIS HONEST. Without it a coach reasonably reads
                "Accept" as "they are now my client", and then wonders why the roster is empty. */}
            <p className="ht-caption requests-note">
              Answering here records your reply. It does not add anyone to your roster — invite
              them from Clients, and they join when they accept the invitation.
            </p>
            <ul className="requests-list ht-panel">
              {rows.map((r) => (
                <li key={r.id} className="requests-row">
                  <p className="requests-name">{describeAsker(r)}</p>
                  {r.note !== null && r.note.trim() !== "" && (
                    <p className="ht-caption requests-note-body">{r.note}</p>
                  )}
                  <p className="requests-aside">{describeStatus(r.status)}</p>
                  {actionFor(r.status) === "decide" && (
                    <div className="requests-actions">
                      <button type="button" className="ht-button"
                              disabled={deciding !== null}
                              onClick={() => { void decide(r.id, true); }}>
                        {deciding === r.id ? "Saving…" : "Accept"}
                      </button>
                      <button type="button" className="ht-button ht-button-quiet"
                              disabled={deciding !== null}
                              onClick={() => { void decide(r.id, false); }}>
                        Decline
                      </button>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          </>
        )}
      </main>
    </Workspace>
  );
}
